import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { User, Lock, Shield, CheckCircle, AlertCircle, Users, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { FoxSpinnerInline } from '../components/ui/FoxSpinner';

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [twoFADialog, setTwoFADialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  
  // Estados para gerenciamento de admins
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ username: '', password: '', confirm_password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, admin: null });
  const [deletingAdmin, setDeletingAdmin] = useState(false);

  useEffect(() => {
    loadProfile();
    loadAdmins();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await adminAPI.getProfile();
      setProfile(response.data);
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const response = await adminAPI.listAdmins();
      setAdmins(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar admins:', error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (newAdminForm.password !== newAdminForm.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newAdminForm.password.length < 8) {
      toast.error('Senha deve ter no mínimo 8 caracteres');
      return;
    }

    if (!/[A-Z]/.test(newAdminForm.password)) {
      toast.error('Senha deve conter pelo menos 1 letra MAIÚSCULA');
      return;
    }

    if (!/[a-z]/.test(newAdminForm.password)) {
      toast.error('Senha deve conter pelo menos 1 letra minúscula');
      return;
    }

    if (!/[0-9]/.test(newAdminForm.password)) {
      toast.error('Senha deve conter pelo menos 1 número');
      return;
    }

    setCreatingAdmin(true);
    try {
      await adminAPI.createAdmin({
        username: newAdminForm.username,
        password: newAdminForm.password
      });
      toast.success('Administrador criado com sucesso!');
      setNewAdminForm({ username: '', password: '', confirm_password: '' });
      setShowAddAdmin(false);
      loadAdmins();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg).join(', ');
        toast.error(`Erro: ${messages}`);
      } else {
        toast.error(errorDetail || 'Erro ao criar administrador');
      }
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!deleteDialog.admin) return;
    
    setDeletingAdmin(true);
    try {
      await adminAPI.deleteAdmin(deleteDialog.admin.id);
      toast.success(`Administrador '${deleteDialog.admin.username}' excluído`);
      setDeleteDialog({ open: false, admin: null });
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir administrador');
    } finally {
      setDeletingAdmin(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Senha deve ter no mínimo 8 caracteres');
      return;
    }

    // Validação de senha forte
    if (!/[A-Z]/.test(passwordForm.new_password)) {
      toast.error('Senha deve conter pelo menos 1 letra MAIÚSCULA');
      return;
    }

    if (!/[a-z]/.test(passwordForm.new_password)) {
      toast.error('Senha deve conter pelo menos 1 letra minúscula');
      return;
    }

    if (!/[0-9]/.test(passwordForm.new_password)) {
      toast.error('Senha deve conter pelo menos 1 número');
      return;
    }

    try {
      console.log('✅ Validações passaram, enviando para API...');
      await adminAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Senha alterada com sucesso! 🎉', { duration: 3000 });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('❌ Erro ao alterar senha:', error);
      console.error('Detalhes:', error.response?.data);
      const errorDetail = error.response?.data?.detail;
      
      // Mensagens de erro específicas
      if (Array.isArray(errorDetail)) {
        // Erros de validação Pydantic
        const messages = errorDetail.map(e => e.msg).join(', ');
        toast.error(`Erro de validação: ${messages}`, { duration: 5000 });
      } else {
        toast.error(errorDetail || 'Erro ao alterar senha', { duration: 5000 });
      }
    }
  };

  const handleEnable2FA = async () => {
    try {
      const response = await adminAPI.enable2FA();
      setSecret(response.data.secret);
      setQrCode(response.data.qr_code);
      setTwoFADialog(true);
    } catch (error) {
      toast.error('Erro ao ativar 2FA');
    }
  };

  const handleVerify2FA = async () => {
    try {
      await adminAPI.verify2FASetup(verifyCode);
      toast.success('2FA ativado com sucesso!');
      setTwoFADialog(false);
      setVerifyCode('');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode) {
      toast.error('Digite o código 2FA');
      return;
    }

    try {
      await adminAPI.disable2FA(disableCode);
      toast.success('2FA desativado');
      setDisableCode('');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    }
  };

  if (loading) {
    return <FoxSpinnerInline size="lg" />;
  }

  return (
    <div className="space-y-6" data-testid="my-profile">
      <div>
        <h2 className="text-2xl font-bold">Meu Perfil</h2>
        <p className="text-gray-600">Gerencie sua conta e segurança</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Informações da Conta */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informações da Conta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Usuário</Label>
              <p className="text-lg font-medium">{profile?.username}</p>
            </div>
            <div>
              <Label>Criado em</Label>
              <p className="text-sm text-gray-600">
                {profile?.created_at && new Date(profile.created_at).toLocaleString('pt-BR')}
              </p>
            </div>
            <div>
              <Label>Último acesso</Label>
              <p className="text-sm text-gray-600">
                {profile?.last_login ? new Date(profile.last_login).toLocaleString('pt-BR') : 'Nunca'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Autenticação de Dois Fatores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Autenticação de Dois Fatores (2FA)
            </CardTitle>
            <CardDescription>
              Aumente a segurança da sua conta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Status</p>
                <p className="text-sm text-gray-600">
                  {profile?.two_factor_enabled ? '2FA está ativo' : '2FA está desativado'}
                </p>
              </div>
              <Badge variant={profile?.two_factor_enabled ? 'default' : 'secondary'}>
                {profile?.two_factor_enabled ? '✓ ATIVO' : 'INATIVO'}
              </Badge>
            </div>

            {!profile?.two_factor_enabled ? (
              <Button
                data-testid="enable-2fa-button"
                onClick={handleEnable2FA}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Shield className="mr-2 h-4 w-4" />
                Ativar 2FA
              </Button>
            ) : (
              <div className="space-y-3">
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Sua conta está protegida com 2FA
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>Código para desativar</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="disable-2fa-code"
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                    />
                    <Button
                      data-testid="disable-2fa-button"
                      onClick={handleDisable2FA}
                      variant="destructive"
                    >
                      Desativar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alterar Senha */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Alterar Senha
          </CardTitle>
          <CardDescription>
            Senha deve ter: mínimo 8 caracteres, 1 maiúscula, 1 minúscula, 1 número
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label>Senha Atual</Label>
              <Input
                data-testid="current-password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Nova Senha</Label>
              <Input
                data-testid="new-password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500">
                Requisitos: 8+ caracteres, 1 MAIÚSCULA, 1 minúscula, 1 número
              </p>
            </div>
            <div className="space-y-2">
              <Label>Confirmar Nova Senha</Label>
              <Input
                data-testid="confirm-password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
              />
            </div>
            <Button data-testid="change-password-button" type="submit" className="bg-purple-600 hover:bg-purple-700">
              Alterar Senha
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* ========================================
          GERENCIAMENTO DE ADMINISTRADORES
          ======================================== */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gerenciar Administradores
              </CardTitle>
              <CardDescription>
                Adicione ou remova administradores do sistema
              </CardDescription>
            </div>
            <Button
              data-testid="add-admin-button"
              onClick={() => setShowAddAdmin(!showAddAdmin)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Admin
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Formulário de Adicionar Admin */}
          {showAddAdmin && (
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="pt-4">
                <form onSubmit={handleCreateAdmin} className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        data-testid="new-admin-username"
                        type="text"
                        placeholder="novo_admin"
                        value={newAdminForm.username}
                        onChange={(e) => setNewAdminForm({ ...newAdminForm, username: e.target.value })}
                        required
                        minLength={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Senha</Label>
                      <div className="relative">
                        <Input
                          data-testid="new-admin-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="********"
                          value={newAdminForm.password}
                          onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Confirmar Senha</Label>
                      <Input
                        data-testid="new-admin-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="********"
                        value={newAdminForm.confirm_password}
                        onChange={(e) => setNewAdminForm({ ...newAdminForm, confirm_password: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">
                    Requisitos da senha: mínimo 8 caracteres, 1 MAIÚSCULA, 1 minúscula, 1 número
                  </p>
                  <div className="flex gap-2">
                    <Button
                      data-testid="create-admin-submit"
                      type="submit"
                      disabled={creatingAdmin}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {creatingAdmin ? 'Criando...' : 'Criar Administrador'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddAdmin(false);
                        setNewAdminForm({ username: '', password: '', confirm_password: '' });
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Lista de Administradores */}
          {loadingAdmins ? (
            <FoxSpinnerInline size="sm" />
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div
                  key={admin.id}
                  data-testid={`admin-item-${admin.id}`}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    admin.id === profile?.id ? 'bg-purple-50 border-purple-300' : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                      {admin.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{admin.username}</p>
                        {admin.id === profile?.id && (
                          <Badge className="bg-purple-600 text-xs">VOCÊ</Badge>
                        )}
                        {admin.two_factor_enabled && (
                          <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            2FA
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Criado: {admin.created_at && new Date(admin.created_at).toLocaleDateString('pt-BR')}
                        {admin.last_login && ` • Último acesso: ${new Date(admin.last_login).toLocaleDateString('pt-BR')}`}
                      </p>
                    </div>
                  </div>
                  {admin.id !== profile?.id && (
                    <Button
                      data-testid={`delete-admin-${admin.id}`}
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteDialog({ open: true, admin })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {admins.length === 0 && (
                <p className="text-center text-gray-500 py-4">Nenhum administrador encontrado</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, admin: deleteDialog.admin })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Confirmar Exclusão
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o administrador <strong>{deleteDialog.admin?.username}</strong>?
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, admin: null })}
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-admin"
              variant="destructive"
              onClick={handleDeleteAdmin}
              disabled={deletingAdmin}
            >
              {deletingAdmin ? 'Excluindo...' : 'Excluir Administrador'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Ativação 2FA */}
      <Dialog open={twoFADialog} onOpenChange={setTwoFADialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ativar Autenticação de Dois Fatores</DialogTitle>
            <DialogDescription>
              Escaneie o QR Code com Google Authenticator
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {qrCode && (
              <div className="flex justify-center">
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="QR Code 2FA"
                  className="w-48 h-48 border rounded"
                />
              </div>
            )}
            <div>
              <Label>Ou digite o código manualmente:</Label>
              <Input
                value={secret}
                readOnly
                className="font-mono text-xs"
              />
            </div>
            <div>
              <Label>Digite o código de 6 dígitos do app:</Label>
              <Input
                data-testid="verify-2fa-code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTwoFADialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="verify-2fa-button"
              onClick={handleVerify2FA}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Verificar e Ativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProfile;
